import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { PowerBIEmbed } from 'powerbi-client-react';
import { models, Report, service } from 'powerbi-client';
import { embedPowerBIReport } from './embedPowerBIReport';
import styles from './PowerBIReportComponent.module.scss';
import { KWELLO_ICON } from 'common/components/icon/KwelloSvgIcon';
import { Spinner } from 'common/components/loader/Spinner';
import { PowerBiReportParams } from 'common/models/power-bi.model';
import KwelloIconButton from '../KwelloButton/KwelloIconButton';
import useNavigationMenus from 'common/hooks/useMenus';
import { useModuleProjectContext } from 'common/contexts/useModuleProjectContext';
import { ProjectParameter } from 'common/models/page-config.models';
import {
  PowerBiFilterStateBridgePage,
  usePowerBiFilterStateBridge,
} from './usePowerBiFilterStateBridge';
import { useSelector } from 'react-redux';
import { selectKwelloUserId } from 'common/store/slices/page-config.slice';

const NAVIGATION_RESTORE_QUIET_PERIOD_MS = 2000;

/**
 * PowerBIReportComponent
 *
 * A reusable component for embedding PowerBI reports (both interactive and paginated) in NextGen.
 * Uses the PowerBI Client SDK to securely embed reports with access tokens.
 *
 * Features:
 * - Interactive and paginated report support
 * - Dynamic pane control (filters, bookmarks, page navigation)
 * - Loading state with Kendo spinner (report hidden until 'rendered' event fires)
 *
 * @see https://learn.microsoft.com/en-us/javascript/api/overview/powerbi/embed-report
 * @see https://learn.microsoft.com/en-us/javascript/api/overview/powerbi/configure-report-settings
 * @see https://learn.microsoft.com/en-us/javascript/api/overview/powerbi/handling-events
 */
interface PowerBIReportComponentProps {
  embedParams: PowerBiReportParams;
  isPaginatedReport?: boolean;
  onError?: (message: string) => void;
  showFilterButton?: boolean;
}

interface PowerBiPageChangedEventDetail {
  newPage?: PowerBiFilterStateBridgePage;
}

interface PowerBiEventLifecycle {
  applyState: ReturnType<typeof usePowerBiFilterStateBridge>['applyState'];
  captureState: ReturnType<typeof usePowerBiFilterStateBridge>['captureState'];
  getCurrentEmbeddedReport: () => Report | null;
  isCurrentEmbeddedReportInstance: (report: Report) => boolean;
  onError?: (message: string) => void;
  scheduleCaptureState: () => void;
  scheduleNavigationRestore: (report: Report) => void;
  sharedDateStateEnabled: boolean;
  startCaptureStatePolling: () => void;
}

export const PowerBIReportComponent: React.FC<PowerBIReportComponentProps> = ({
  embedParams,
  isPaginatedReport = false,
  onError,
}) => {
  const [filterPaneEnabled, setFilterPaneEnabled] = useState(false);
  const [isReportVisible, setIsReportVisible] = useState(false);
  const [bookmarksVisible, setBookmarksVisible] = useState(false);
  const [pageNavVisible, setPageNavVisible] = useState(false);
  const reportRef = useRef<HTMLDivElement>(null);
  const embedInstanceRef = useRef<Report | null>(null);
  const activeReportContextKeyRef = useRef<string | null>(null);
  const embedInstanceReportContextKeyRef = useRef<string | null>(null);
  const activePageContextKeyRef = useRef<string | null>(null);
  const previousPageContextKeyRef = useRef<string | null>(null);
  const loadedReportRef = useRef<Report | null>(null);
  const renderStartedReportRef = useRef<Report | null>(null);
  const targetPageNameRef = useRef(embedParams.pageName);
  const initialPageRef = useRef<{ reportContextKey: string; pageName?: string } | null>(null);
  const applyStatePromiseRef = useRef<Promise<void>>(Promise.resolve());
  const captureStateTimeoutRef = useRef<number | null>(null);
  const captureStateIntervalRef = useRef<number | null>(null);
  const navigationRestoreTimeoutRef = useRef<number | null>(null);
  const captureStatePausedRef = useRef(false);
  const captureGenerationRef = useRef(0);
  const awaitingPageChangeRef = useRef(false);
  const eventLifecycleRef = useRef<PowerBiEventLifecycle | null>(null);
  const [embedInstance, setEmbedInstance] = useState<Report | null>(null);
  const { currentPageDetails } = useNavigationMenus();
  const { activeProject, getProjectParameter } = useModuleProjectContext();
  const kwelloUserId = useSelector(selectKwelloUserId);
  const projectId = activeProject?.moduleProjectID;
  const sharedDateStateEnabled =
    !isPaginatedReport &&
    (activeProject?.pages?.filter((page) => page.PowerBIReportID === embedParams.reportId).length ??
      0) > 1;
  const reportContextKey = useMemo(
    () =>
      [
        isPaginatedReport ? 'paginated' : 'interactive',
        projectId ?? '',
        embedParams.reportId ?? '',
      ].join('|'),
    [embedParams.reportId, isPaginatedReport, projectId]
  );
  const pageContextKey = `${reportContextKey}|${embedParams.pageName ?? ''}`;
  if (initialPageRef.current?.reportContextKey !== reportContextKey) {
    initialPageRef.current = {
      reportContextKey,
      pageName: embedParams.pageName,
    };
  }
  const initialPageName = initialPageRef.current.pageName;
  targetPageNameRef.current = embedParams.pageName;

  const showFiltersButton = getProjectParameter(ProjectParameter.ShowEmbeddedFilters);
  const showBookmarksButton = getProjectParameter(ProjectParameter.ShowEmbeddedBookmarks);
  const showPagesButton = getProjectParameter(ProjectParameter.ShowEmbeddedPages);
  const { applyState, captureState, clearState, hasUserChangedDate } = usePowerBiFilterStateBridge({
    report: embedInstance,
    userKey: kwelloUserId,
    projectId,
    reportId: embedParams.reportId,
    pageName: embedParams.pageName,
    enabled: sharedDateStateEnabled,
  });
  const showPowerBiHeader =
    !isPaginatedReport &&
    (showFiltersButton || showBookmarksButton || showPagesButton || hasUserChangedDate);

  useLayoutEffect(() => {
    activeReportContextKeyRef.current = reportContextKey;
    loadedReportRef.current = null;
    renderStartedReportRef.current = null;
    setIsReportVisible(false);
  }, [reportContextKey]);

  useLayoutEffect(() => {
    awaitingPageChangeRef.current =
      previousPageContextKeyRef.current !== null &&
      previousPageContextKeyRef.current !== pageContextKey;
    previousPageContextKeyRef.current = pageContextKey;
    activePageContextKeyRef.current = pageContextKey;
    captureGenerationRef.current += 1;
    applyStatePromiseRef.current = Promise.resolve();
    captureStatePausedRef.current = false;
    if (captureStateTimeoutRef.current !== null) {
      window.clearTimeout(captureStateTimeoutRef.current);
      captureStateTimeoutRef.current = null;
    }
    if (captureStateIntervalRef.current !== null) {
      window.clearInterval(captureStateIntervalRef.current);
      captureStateIntervalRef.current = null;
    }
    if (navigationRestoreTimeoutRef.current !== null) {
      window.clearTimeout(navigationRestoreTimeoutRef.current);
      navigationRestoreTimeoutRef.current = null;
    }
  }, [pageContextKey]);

  useEffect(
    () => () => {
      if (captureStateTimeoutRef.current !== null) {
        window.clearTimeout(captureStateTimeoutRef.current);
      }
      if (captureStateIntervalRef.current !== null) {
        window.clearInterval(captureStateIntervalRef.current);
      }
      if (navigationRestoreTimeoutRef.current !== null) {
        window.clearTimeout(navigationRestoreTimeoutRef.current);
      }
    },
    []
  );

  const getCurrentEmbeddedReport = useCallback(() => {
    const report = embedInstanceRef.current;
    const isCurrentReportContext =
      activeReportContextKeyRef.current === reportContextKey &&
      embedInstanceReportContextKeyRef.current === reportContextKey;

    if (!report || !isCurrentReportContext || activePageContextKeyRef.current !== pageContextKey) {
      return null;
    }

    return report;
  }, [pageContextKey, reportContextKey]);

  const isCurrentEmbeddedReportInstance = useCallback(
    (report: Report) =>
      embedInstanceRef.current === report &&
      activeReportContextKeyRef.current === reportContextKey &&
      embedInstanceReportContextKeyRef.current === reportContextKey,
    [reportContextKey]
  );

  const scheduleCaptureState = useCallback(() => {
    if (!sharedDateStateEnabled || captureStatePausedRef.current) {
      return;
    }

    if (captureStateTimeoutRef.current !== null) {
      window.clearTimeout(captureStateTimeoutRef.current);
    }

    const scheduledPageContextKey = pageContextKey;
    const scheduledCaptureGeneration = captureGenerationRef.current;
    captureStateTimeoutRef.current = window.setTimeout(async () => {
      captureStateTimeoutRef.current = null;
      if (
        captureStatePausedRef.current ||
        captureGenerationRef.current !== scheduledCaptureGeneration ||
        activePageContextKeyRef.current !== scheduledPageContextKey
      ) {
        return;
      }
      const report = getCurrentEmbeddedReport();

      if (!report) {
        return;
      }

      await applyStatePromiseRef.current;
      if (
        captureStatePausedRef.current ||
        captureGenerationRef.current !== scheduledCaptureGeneration ||
        activePageContextKeyRef.current !== scheduledPageContextKey
      ) {
        return;
      }
      await captureState(
        report,
        () =>
          !captureStatePausedRef.current &&
          captureGenerationRef.current === scheduledCaptureGeneration &&
          activePageContextKeyRef.current === scheduledPageContextKey
      );
    }, 250);
  }, [captureState, getCurrentEmbeddedReport, pageContextKey, sharedDateStateEnabled]);

  const startCaptureStatePolling = useCallback(() => {
    if (!sharedDateStateEnabled) {
      return;
    }

    if (captureStateIntervalRef.current !== null) {
      window.clearInterval(captureStateIntervalRef.current);
    }

    captureStateIntervalRef.current = window.setInterval(() => {
      scheduleCaptureState();
    }, 1000);
  }, [scheduleCaptureState, sharedDateStateEnabled]);

  const scheduleNavigationRestore = useCallback((report: Report) => {
    if (!awaitingPageChangeRef.current) {
      return;
    }
    if (navigationRestoreTimeoutRef.current !== null) {
      window.clearTimeout(navigationRestoreTimeoutRef.current);
    }

    const scheduledPageContextKey = activePageContextKeyRef.current;
    navigationRestoreTimeoutRef.current = window.setTimeout(async () => {
      navigationRestoreTimeoutRef.current = null;
      const lifecycle = eventLifecycleRef.current;
      if (
        !lifecycle ||
        !awaitingPageChangeRef.current ||
        lifecycle.getCurrentEmbeddedReport() !== report ||
        activePageContextKeyRef.current !== scheduledPageContextKey
      ) {
        return;
      }

      const settledApplyStatePromise = lifecycle.applyState(report);
      applyStatePromiseRef.current = settledApplyStatePromise;
      await settledApplyStatePromise;
      if (
        lifecycle.getCurrentEmbeddedReport() !== report ||
        activePageContextKeyRef.current !== scheduledPageContextKey
      ) {
        return;
      }

      awaitingPageChangeRef.current = false;
      lifecycle.startCaptureStatePolling();
    }, NAVIGATION_RESTORE_QUIET_PERIOD_MS);
  }, []);

  eventLifecycleRef.current = {
    applyState,
    captureState,
    getCurrentEmbeddedReport,
    isCurrentEmbeddedReportInstance,
    onError,
    scheduleCaptureState,
    scheduleNavigationRestore,
    sharedDateStateEnabled,
    startCaptureStatePolling,
  };

  const handleClearState = useCallback(() => {
    captureGenerationRef.current += 1;
    captureStatePausedRef.current = true;
    if (captureStateTimeoutRef.current !== null) {
      window.clearTimeout(captureStateTimeoutRef.current);
      captureStateTimeoutRef.current = null;
    }
    if (captureStateIntervalRef.current !== null) {
      window.clearInterval(captureStateIntervalRef.current);
      captureStateIntervalRef.current = null;
    }
    clearState();
  }, [clearState]);

  // Memoize embed config to prevent unnecessary re-renders when pane states change.
  // Pane visibility is controlled dynamically via updateSettings() to avoid re-embedding.
  // @see https://learn.microsoft.com/en-us/javascript/api/overview/powerbi/embed-report#embed-a-report
  const defaultReportConfig = useMemo(
    () => ({
      type: 'report',
      id: embedParams.reportId,
      pageName: initialPageName,
      embedUrl: embedParams.embedUrl,
      tokenType: models.TokenType.Embed,
      accessToken: embedParams.embedToken?.token ?? '',
      settings: {
        panes: {
          filters: {
            visible: false,
          },
        },
        visualRenderedEvents: true,
        navContentPaneEnabled: false,
        // Don't set background to Transparent - let the report use its own saved background colors
        // from Power BI Desktop (canvas and wallpaper settings)
      },
    }),
    [embedParams.reportId, embedParams.embedUrl, embedParams.embedToken?.token, initialPageName]
  );

  useEffect(() => {
    const report = getCurrentEmbeddedReport();
    const targetPageName = embedParams.pageName;

    if (!report || !targetPageName || loadedReportRef.current !== report) {
      return;
    }

    let cancelled = false;
    void report
      .getActivePage()
      .then((activePage) => {
        const latestTargetPageName = targetPageNameRef.current;
        if (
          cancelled ||
          getCurrentEmbeddedReport() !== report ||
          !latestTargetPageName ||
          !activePage ||
          activePage.name === latestTargetPageName
        ) {
          return;
        }

        setIsReportVisible(false);
        return report.setPage(latestTargetPageName).then(async () => {
          const lifecycle = eventLifecycleRef.current;
          if (
            cancelled ||
            !lifecycle ||
            lifecycle.getCurrentEmbeddedReport() !== report ||
            targetPageNameRef.current !== latestTargetPageName
          ) {
            return;
          }

          const postNavigationApplyStatePromise = lifecycle.applyState(report);
          applyStatePromiseRef.current = postNavigationApplyStatePromise;
          await postNavigationApplyStatePromise;
          lifecycle.scheduleNavigationRestore(report);
        });
      })
      .catch((error: unknown) => {
        if (!cancelled) {
          setIsReportVisible(true);
          console.error('Failed to navigate the embedded Power BI report page:', error);
          onError?.(String(error));
        }
      });

    return () => {
      cancelled = true;
    };
  }, [
    embedInstance,
    embedParams.pageName,
    getCurrentEmbeddedReport,
    onError,
    pageContextKey,
    reportContextKey,
  ]);

  // Event handlers for PowerBI embed lifecycle events.
  // Phased embedding keeps saved report defaults hidden while 'loaded' applies shared state,
  // then report.render() starts visual rendering with that state already in place.
  // 'rendered': All visuals rendered - safe to show report.
  // @see https://learn.microsoft.com/en-us/javascript/api/overview/powerbi/phased-embedding
  // @see https://learn.microsoft.com/en-us/javascript/api/overview/powerbi/handle-events
  const eventHandlersMap = useMemo<
    Map<string, (event?: service.ICustomEvent<unknown>) => void | Promise<void>>
  >(
    () =>
      new Map([
        [
          'loaded',
          async () => {
            const lifecycle = eventLifecycleRef.current;
            const report = lifecycle?.getCurrentEmbeddedReport();

            if (!lifecycle || !report) {
              return;
            }

            try {
              const applyStatePromise = lifecycle.applyState(report);
              applyStatePromiseRef.current = applyStatePromise;
              await applyStatePromise;
              if (!lifecycle.isCurrentEmbeddedReportInstance(report)) {
                return;
              }
              if (renderStartedReportRef.current !== report) {
                renderStartedReportRef.current = report;
                await report.render();
                setIsReportVisible(true);
              }
              if (!lifecycle.isCurrentEmbeddedReportInstance(report)) {
                return;
              }
              loadedReportRef.current = report;

              const activePage = await report.getActivePage();
              const targetPageName = targetPageNameRef.current;
              if (
                targetPageName &&
                activePage &&
                activePage.name !== targetPageName &&
                lifecycle.isCurrentEmbeddedReportInstance(report)
              ) {
                setIsReportVisible(false);
                await report.setPage(targetPageName);
                if (lifecycle.isCurrentEmbeddedReportInstance(report)) {
                  const postNavigationApplyStatePromise = lifecycle.applyState(report);
                  applyStatePromiseRef.current = postNavigationApplyStatePromise;
                  await postNavigationApplyStatePromise;
                  lifecycle.scheduleNavigationRestore(report);
                }
              }
            } catch (error: unknown) {
              console.error('Failed to complete the embedded Power BI load lifecycle:', error);
              lifecycle.onError?.(String(error));
            }
          },
        ],
        [
          'pageChanged',
          async (event?: service.ICustomEvent<unknown>) => {
            const lifecycle = eventLifecycleRef.current;
            const report = lifecycle?.getCurrentEmbeddedReport();
            const destinationPage = (event?.detail as PowerBiPageChangedEventDetail | undefined)
              ?.newPage;

            if (!lifecycle || !report) {
              return;
            }

            if (
              awaitingPageChangeRef.current &&
              destinationPage?.name &&
              destinationPage.name !== targetPageNameRef.current
            ) {
              return;
            }

            // Microsoft documents event.detail.newPage as the authoritative destination
            // Page. Re-querying report.getActivePage() here can still return the departing
            // page while setPage() is completing, applying shared state to the wrong page.
            const applyStatePromise = lifecycle.applyState(report, destinationPage);
            applyStatePromiseRef.current = applyStatePromise;
            await applyStatePromise;
          },
        ],
        [
          'rendered',
          async () => {
            const lifecycle = eventLifecycleRef.current;
            const report = lifecycle?.getCurrentEmbeddedReport();
            const renderedCaptureGeneration = captureGenerationRef.current;

            if (!lifecycle || !report) {
              return;
            }

            // The SDK has rendered the destination page. Do not leave it behind
            // the Kwello spinner while slower slicer APIs finish restoring state.
            setIsReportVisible(true);
            await applyStatePromiseRef.current;
            if (awaitingPageChangeRef.current) {
              lifecycle.scheduleNavigationRestore(report);
              return;
            }
            if (captureStatePausedRef.current || !lifecycle.getCurrentEmbeddedReport()) {
              return;
            }
            if (!lifecycle.sharedDateStateEnabled) {
              return;
            }
            const renderedPageContextKey = activePageContextKeyRef.current;
            await lifecycle.captureState(
              report,
              () =>
                !captureStatePausedRef.current &&
                captureGenerationRef.current === renderedCaptureGeneration &&
                activePageContextKeyRef.current === renderedPageContextKey
            );
            if (
              captureStatePausedRef.current ||
              captureGenerationRef.current !== renderedCaptureGeneration ||
              activePageContextKeyRef.current !== renderedPageContextKey
            ) {
              return;
            }
            lifecycle.startCaptureStatePolling();
          },
        ],
        [
          'visualRendered',
          async () => {
            const lifecycle = eventLifecycleRef.current;
            setIsReportVisible(true);
            const report = lifecycle?.getCurrentEmbeddedReport();

            if (!lifecycle || !report) {
              return;
            }

            await applyStatePromiseRef.current;
            if (awaitingPageChangeRef.current) {
              lifecycle.scheduleNavigationRestore(report);
              return;
            }
            lifecycle.scheduleCaptureState();
          },
        ],
        [
          'error',
          (event?: service.ICustomEvent<unknown>) => {
            if (event?.detail) {
              if (isPowerBiVisualRenderError(event.detail)) {
                setIsReportVisible(true);
              }
              console.error('Report error:', event.detail);
              eventLifecycleRef.current?.onError?.(String(event.detail));
            }
          },
        ],
      ]),
    []
  );

  // Handle paginated report embedding using powerbi-client service.
  // Paginated reports use a different embed method and don't support phased loading events.
  // @see https://learn.microsoft.com/en-us/javascript/api/overview/powerbi/embed-paginated-report
  useEffect(() => {
    if (isPaginatedReport && reportRef.current && embedParams.reportId) {
      setIsReportVisible(true);
      embedPowerBIReport({
        reportContainer: reportRef.current,
        reportConfig: defaultReportConfig,
        onError: (message: string) => {
          console.error('Paginated report error:', message);
          onError?.(message);
        },
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [embedParams, isPaginatedReport]);

  // Use updateSettings() to toggle panes dynamically without re-embedding the report.
  // @see https://learn.microsoft.com/en-us/javascript/api/overview/powerbi/configure-report-settings#update-settings-at-runtime
  const toggleFilters = async () => {
    const newFilterState = !filterPaneEnabled;
    setFilterPaneEnabled(newFilterState);

    if (embedInstanceRef.current) {
      try {
        await embedInstanceRef.current.updateSettings({
          panes: {
            filters: {
              visible: newFilterState,
            },
          },
        });
      } catch (error) {
        console.error('Failed to update filter pane visibility:', error);
      }
    }
  };

  const toggleBookmarks = async () => {
    const newBookmarksState = !bookmarksVisible;
    setBookmarksVisible(newBookmarksState);

    if (embedInstanceRef.current) {
      try {
        await embedInstanceRef.current.updateSettings({
          panes: {
            bookmarks: {
              visible: newBookmarksState,
            },
          },
        });
      } catch (error) {
        console.error('Failed to update bookmarks pane visibility:', error);
      }
    }
  };

  const togglePageNav = async () => {
    const newPageNavState = !pageNavVisible;
    setPageNavVisible(newPageNavState);

    if (embedInstanceRef.current) {
      try {
        await embedInstanceRef.current.updateSettings({
          panes: {
            pageNavigation: {
              visible: newPageNavState,
            },
          },
        });
      } catch (error) {
        console.error('Failed to update page navigation visibility:', error);
      }
    }
  };

  return (
    <>
      {/* Header with controls */}
      {showPowerBiHeader && (
        <div className={styles['powerbi-header']}>
          <div className={styles['title-container']}>
            <span className={styles['title']}>{currentPageDetails?.text ?? 'Embedded Report'}</span>
          </div>
          <div className={styles['header-actions']}>
            {showFiltersButton && embedParams.reportId && (
              <KwelloIconButton
                icon={KWELLO_ICON.FILTER_ICON}
                onClick={toggleFilters}
                tooltip={filterPaneEnabled ? 'Hide Filters' : 'Show Filters'}
                size="small"
                active={filterPaneEnabled}
              />
            )}

            {showBookmarksButton && (
              <KwelloIconButton
                icon={KWELLO_ICON.BOOKMARK_ICON}
                onClick={toggleBookmarks}
                tooltip={bookmarksVisible ? 'Hide Bookmarks' : 'Show Bookmarks'}
                size="small"
                active={bookmarksVisible}
              />
            )}

            {showPagesButton && (
              <KwelloIconButton
                icon={KWELLO_ICON.LIST_ICON}
                onClick={togglePageNav}
                tooltip={pageNavVisible ? 'Hide Pages' : 'Show Pages'}
                size="small"
                active={pageNavVisible}
              />
            )}

            {hasUserChangedDate && (
              <KwelloIconButton
                icon={KWELLO_ICON.TRASH_BIN_ICON}
                onClick={handleClearState}
                tooltip="Clear saved filters"
                size="small"
              />
            )}
          </div>
        </div>
      )}

      {/* Interactive Report */}
      {!isPaginatedReport && (
        <div className={styles['powerbi-container']}>
          {/* Show Kendo spinner until report is fully rendered */}
          {!isReportVisible && (
            <div className={styles['powerbi-loading']}>
              <Spinner size="large" />
            </div>
          )}

          <PowerBIEmbed
            embedConfig={defaultReportConfig}
            eventHandlers={eventHandlersMap}
            phasedEmbedding
            cssClassName={
              isReportVisible ? styles['powerbi-report'] : styles['powerbi-report-hidden']
            }
            getEmbeddedComponent={(embeddedReport) => {
              const report = embeddedReport as Report;
              if (embedInstanceRef.current !== report) {
                loadedReportRef.current = null;
                renderStartedReportRef.current = null;
                setIsReportVisible(false);
              }
              activeReportContextKeyRef.current = reportContextKey;
              embedInstanceReportContextKeyRef.current = reportContextKey;
              embedInstanceRef.current = report;
              setEmbedInstance(report);
            }}
          />
        </div>
      )}

      {/* Paginated Report */}
      {isPaginatedReport && <div className={styles['powerbi-report']} ref={reportRef} />}
    </>
  );
};

const isPowerBiVisualRenderError = (detail: unknown) => {
  if (!detail || typeof detail !== 'object') {
    return false;
  }

  const error = detail as { detailedMessage?: unknown };

  return (
    typeof error.detailedMessage === 'string' &&
    error.detailedMessage.toLowerCase().includes('could not render a report visual')
  );
};
